// Direct TS port of source_code/apps/lume/Lumé/Services/SkinAgeCalculator.swift
// Keep behavior aligned 1:1 with iOS so cross-platform results match.

import type { SkinScores, SkinFactor, Severity } from './types';

export function computeSkinAge(scores: SkinScores): number {
  const wrinkle = scores.wrinkles.overall;
  const pigment = scores.pigmentation.overall;
  const texture = scores.texture.overall;
  const pores = scores.pores.overall;
  const dehydration = scores.hydration ? 100 - scores.hydration.overall : 50;
  const firmLoss = scores.firmness ? 100 - scores.firmness.overall : 50;

  const agingScore =
    wrinkle * 0.35 +
    pigment * 0.22 +
    texture * 0.15 +
    pores * 0.10 +
    dehydration * 0.10 +
    firmLoss * 0.08;

  // [0, 100] → skin age [18, 45]
  return Math.round(18 + agingScore * 0.27);
}

export function severityFromScore(score: number): Severity {
  if (score >= 65) return 'high';
  if (score >= 35) return 'moderate';
  return 'healthy';
}

function wrinkleInsight(score: number): string {
  if (score >= 65) return 'Significant photoaging lines, particularly around the eyes';
  if (score >= 40) return 'Early fine lines visible in expression areas';
  return 'Minimal fine lines — strong collagen activity';
}

function pigmentInsight(score: number): string {
  if (score >= 65) return 'UV exposure is your primary age accelerator';
  if (score >= 40) return 'Moderate uneven tone with sun-related spots';
  return 'Relatively even skin tone';
}

function severityOrder(s: Severity): number {
  return s === 'high' ? 0 : s === 'moderate' ? 1 : 2;
}

export function factorsFrom(scores: SkinScores): SkinFactor[] {
  const out: SkinFactor[] = [];

  if (scores.wrinkles.overall > 15) {
    out.push({
      name: 'Fine lines',
      icon: 'lines',
      severity: severityFromScore(scores.wrinkles.overall),
      insight: wrinkleInsight(scores.wrinkles.overall),
    });
  }
  if (scores.pigmentation.overall > 20) {
    out.push({
      name: 'Sun damage',
      icon: 'sun',
      severity: severityFromScore(scores.pigmentation.overall),
      insight: pigmentInsight(scores.pigmentation.overall),
    });
  }
  if (scores.redness.overall > 25) {
    out.push({
      name: 'Redness',
      icon: 'drop',
      severity: severityFromScore(scores.redness.overall),
      insight: 'Reactive redness or low-grade inflammation present',
    });
  }
  if (scores.hydration && scores.hydration.overall < 55) {
    out.push({
      name: 'Hydration',
      icon: 'drop',
      severity: severityFromScore(100 - scores.hydration.overall),
      insight: 'Skin barrier needs moisture support',
    });
  }
  if (scores.texture.overall > 30) {
    out.push({
      name: 'Texture',
      icon: 'grid',
      severity: severityFromScore(scores.texture.overall),
      insight: 'Uneven surface texture and roughness detected',
    });
  }
  if (scores.pores.overall > 40) {
    out.push({
      name: 'Pores',
      icon: 'circle-grid',
      severity: severityFromScore(scores.pores.overall),
      insight: 'Visible pore enlargement, particularly in the T-zone',
    });
  }

  return out.sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
}

// Per-factor commentary, mirrored from iOS BreakdownView.FactorCommentary
export function factorCommentary(factor: SkinFactor): string | null {
  switch (factor.name) {
    case 'Fine lines':
      return factor.severity === 'healthy'
        ? 'Your collagen is holding up well. Maintain with daily SPF and a peptide moisturizer to keep it that way.'
        : 'Fine lines deepen ~4× faster after age 30 without targeted intervention. Retinol and peptides rebuild collagen — without them, today’s fine lines become tomorrow’s set wrinkles.';
    case 'Sun damage':
      return factor.severity === 'healthy'
        ? 'Your pigmentation is even — keep it that way with broad-spectrum SPF every morning, even indoors.'
        : 'Dark spots darken ~25% per year without treatment. Vitamin C plus daily SPF actively reverses pigmentation; without them, new spots form even when you’re not in direct sun.';
    case 'Hydration':
      return 'A dehydrated barrier accelerates every other sign of aging — wrinkles look deeper, redness flares more, pores look larger. Hydration is foundational; this is one of the fastest visible changes you’ll see.';
    case 'Pores':
      return 'Pore size is partly genetic, but visible pores come from sebum buildup and stretched skin around them. Salicylic acid clears the buildup; niacinamide gradually tightens the surrounding skin.';
    case 'Texture':
      return 'Rough texture is the earliest signal of cell turnover slowing down. Exfoliating acids restore smoothness — one of the fastest visible improvements possible, usually within 2–3 weeks.';
    case 'Redness':
      return factor.severity === 'healthy'
        ? 'Your skin barrier is calm — keep it that way by avoiding harsh exfoliants and over-cleansing.'
        : 'Persistent redness signals barrier damage and low-grade inflammation. Niacinamide and azelaic acid calm reactive skin without further irritation.';
    default:
      return null;
  }
}
